import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { kUntitledDraftName } from '@/drafting/constants'
import { kStaleDraftsTimestamp } from './slice'

/**
 * Select: whether a conversation contributed a draft to a matter's list.
 *
 * Answers the invalidation listener's question: would this conversation's change affect cached drafts?
 */
export function selectMatterHasDraftsForConversation(state: RootState, props: { matterId: string; conversationId: string }): boolean {
  const { conversationIdByDraftArtifactVersionId, matterArtifactDraftVersionIdsByMatterId } = state.draftingState
  const isForConversation = (versionId: string) => conversationIdByDraftArtifactVersionId[versionId] === props.conversationId

  return matterArtifactDraftVersionIdsByMatterId[props.matterId]?.some(isForConversation) ?? false
}

/**
 * A single row of the Drafts destination, projected from one draft head.
 *
 * Creator is the user id, not a display name: names come from the matter's already-loaded members
 * at render time, so a renamed or removed member never leaves a stale copy in the store.
 */
export interface MatterArtifactDraftRow {
  matterArtifactId: string
  /** The artifact's current version at the last fetch — the version a row action operates on. */
  artifactVersionId: string
  /** The conversation the draft opens in. */
  conversationId: string
  name: string
  updatedAt: number
  createdByUserId: string
}

const kNoMatterArtifactDraftRows: MatterArtifactDraftRow[] = []

/**
 * Select: the active matter's draft rows, ordered most-recently-updated first.
 *
 * A row needs its version record and source conversation to be renderable and openable, so an id
 * missing either is skipped rather than rendered incomplete.
 */
export const selectMatterArtifactDraftRows = createSelector(
  [
    (state: RootState, props: { matterId: string | null }) =>
      props.matterId ? state.draftingState.matterArtifactDraftVersionIdsByMatterId[props.matterId] : undefined,
    (state: RootState) => state.draftingState.conversationIdByDraftArtifactVersionId,
    (state: RootState) => state.matterArtifactsState.versionsById,
  ],
  (ids, conversationIdByDraftArtifactVersionId, matterArtifactVersions): MatterArtifactDraftRow[] => {
    if (!ids || ids.length === 0) return kNoMatterArtifactDraftRows

    const rows: MatterArtifactDraftRow[] = []
    for (const id of ids) {
      const version = matterArtifactVersions[id]
      const conversationId = conversationIdByDraftArtifactVersionId[id]
      if (!version || !conversationId) continue

      rows.push({
        matterArtifactId: version.matter_artifact_id,
        artifactVersionId: version.id,
        conversationId,
        name: version.name ?? kUntitledDraftName,
        updatedAt: version.updated_at,
        createdByUserId: version.created_by_user_id,
      })
    }

    if (rows.length === 0) return kNoMatterArtifactDraftRows
    // Mirrors the endpoint's own ordering. The trailing id comparison keeps rows from swapping
    // places between renders when two drafts share a timestamp.
    return rows.sort((a, b) => b.updatedAt - a.updatedAt || a.artifactVersionId.localeCompare(b.artifactVersionId))
  }
)

/**
 * Select: whether a matter's drafts fetch is currently in flight.
 */
export const selectMatterArtifactDraftsLoadingForMatter = (state: RootState, props: { matterId: string | null }): boolean => {
  if (!props.matterId) return false
  return state.draftingState.matterArtifactDraftsLoadingByMatterId[props.matterId] ?? false
}

/**
 * Select: whether a matter's last drafts fetch failed.
 */
export const selectMatterArtifactDraftsFetchFailedForMatter = (state: RootState, props: { matterId: string | null }): boolean => {
  if (!props.matterId) return false
  return state.draftingState.matterArtifactDraftsErrorByMatterId[props.matterId] ?? false
}

/**
 * Select: whether a matter's drafts cache has been invalidated and needs refetching.
 */
export const selectMatterArtifactDraftsStaleForMatter = (state: RootState, props: { matterId: string | null }): boolean => {
  if (!props.matterId) return false
  return state.draftingState.matterArtifactDraftsLastRefreshByMatterId[props.matterId] === kStaleDraftsTimestamp
}

/**
 * Select: when a matter's last drafts fetch finished, successful or not.
 *
 * Null when none has finished, or when the cache was invalidated after the last one, so a changed
 * value always means a fetch has just settled.
 */
export const selectMatterArtifactDraftsLastFetchFinishedAtForMatter = (state: RootState, props: { matterId: string | null }): number | null => {
  if (!props.matterId) return null
  const lastRefresh = state.draftingState.matterArtifactDraftsLastRefreshByMatterId[props.matterId]
  return lastRefresh === undefined || lastRefresh === kStaleDraftsTimestamp ? null : lastRefresh
}

/**
 * Select: whether a matter's drafts initial load has finished (at least one fetch has finished).
 *
 * A failed fetch counts: this answers "is a request still outstanding", not "did one work". For the
 * latter see `selectMatterArtifactDraftsEverLoadedForMatter`.
 */
export const selectMatterArtifactDraftsInitialLoadCompleteForMatter = (state: RootState, props: { matterId: string | null }): boolean => {
  if (!props.matterId) return false
  return state.draftingState.matterArtifactDraftsLastRefreshByMatterId[props.matterId] !== undefined
}

/**
 * Select: whether a fetch has ever succeeded for a matter's drafts.
 *
 * The id list is written only on success, so its presence is the record of one. An empty list is a
 * success like any other: it means the matter has no drafts, which is an answer the UI can show.
 */
export const selectMatterArtifactDraftsEverLoadedForMatter = (state: RootState, props: { matterId: string | null }): boolean => {
  if (!props.matterId) return false
  return state.draftingState.matterArtifactDraftVersionIdsByMatterId[props.matterId] !== undefined
}
